import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaFilePdf, FaRegFileAlt } from 'react-icons/fa';
import { sortArticlesBySequence } from '../services/firebaseService';
import { getArticlePageUrl } from '../utils/journalUrlHelper';
import { getBestFileUrl } from '../utils/fileUrlConverter';
import { getJournalTypeLabel } from '../config/journalTypes';
import { formatArticlePages } from '../utils/articlePages';

// Section headings shown for each article type
const TYPE_TITLES = {
  editorialNote: 'Editorial Note',
  originalArticles: 'Original Research Articles',
  reviewArticles: 'Review Articles',
  caseReports: 'Case Studies',
  philosophicalInsights: 'Philosophical Insights',
  seminarReport: 'Seminar Report',
};

const getTypeTitle = (type) => TYPE_TITLES[type] || (type ? getJournalTypeLabel(type) : 'Articles');

/**
 * An issue's articles grouped by type (Editorial Note, Review Articles, ...).
 * Articles are shown strictly in the sequence set in the admin panel; consecutive articles of the
 * same type share a heading (so a type can appear again further down if the sequence says so).
 */
const IssueArticleGroups = ({ articles, emptyMessage = 'No articles found.' }) => {
  const groups = useMemo(() => {
    const sorted = sortArticlesBySequence(articles || []);
    const runs = [];
    sorted.forEach((article, index) => {
      const type = article.type || 'other';
      const last = runs[runs.length - 1];
      if (last && last.type === type) {
        last.items.push({ article, index });
      } else {
        runs.push({ key: `${type}-${index}`, type, title: getTypeTitle(type === 'other' ? '' : type), items: [{ article, index }] });
      }
    });
    return runs;
  }, [articles]);

  if (groups.length === 0) {
    return <div className="p-8 text-center text-gray-500 text-sm">{emptyMessage}</div>;
  }

  return (
    <div className="p-4 sm:p-5 space-y-3 bg-white">
      {groups.map(({ key, title, items }) => (
        <div key={key} className="rounded-2xl overflow-hidden bg-[#F8FAFC] p-3 sm:p-3.5 border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-[#03478E] bg-blue-50 px-2.5 py-0.5 rounded-full">
              {title}
            </span>
          </div>

          <div className="space-y-1.5">
            {items.map(({ article, index }) => {
              const pageUrl = getArticlePageUrl(article, index);
              const pdfLink = getBestFileUrl(article);
              const pagesText = formatArticlePages(article);
              return (
                <div
                  key={article.id || article.title}
                  className="px-3 py-2 bg-white rounded-xl border border-gray-100 hover:border-blue-200 transition-all flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <Link
                      to={pageUrl}
                      className="text-base font-normal text-gray-900 hover:text-[#03478E] hover:underline block leading-snug"
                    >
                      {article.title}
                    </Link>
                    <div className="text-sm text-gray-600 mt-0.5 flex flex-wrap items-center gap-1.5">
                      <span className="font-normal italic text-gray-600">
                        {article.authors?.startsWith('Dr.') ? article.authors : `Dr. ${article.authors || 'Author'}`}
                      </span>
                    </div>
                  </div>

                  {(pdfLink || pagesText) && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {pagesText && (
                        <span className="inline-flex items-center justify-center gap-1.5 min-w-[5.75rem] text-xs text-gray-600 bg-gray-50 border border-gray-200 px-2.5 py-1 rounded-lg whitespace-nowrap">
                          <FaRegFileAlt className="text-gray-400" aria-hidden="true" />
                          {pagesText}
                        </span>
                      )}
                      {pdfLink && (
                      <a
                        href={pdfLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#03478E] bg-blue-50/70 hover:bg-[#03478E] hover:text-white border border-blue-200 hover:border-[#03478E] px-2.5 py-1 rounded-lg transition-all duration-150 group"
                      >
                        <FaFilePdf className="text-red-500 group-hover:text-white text-xs transition-colors" />
                        <span>Get PDF</span>
                      </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default IssueArticleGroups;
